import { CheckCircleTwoTone, ClockCircleTwoTone, StopTwoTone, WarningTwoTone } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Space, Statistic, Table, Typography } from "antd";
import { ColumnsType, FilterValue } from "antd/lib/table/interface";
import KeyValueTableFilter from "components/KeyValueTableFilter";
import MultiValueDisplay from "components/MultiValueDisplay";
import { useAuthenticatedRequest } from "hooks/useRequests";
import { ClearTableFilterRequest, InitialPageRequest, PagedResultWithExtra, PageRequest, PageSizeOptions } from "models/common";
import { EndListingQueueItem, EndListingQueueStatus } from "models/packages";
import { useState } from "react";
import { pageRequestToUrlSearchParams } from "util/helpers";
import { createCustomFilter, ExtendedFilterDropdownProps } from "util/table";

const { Text } = Typography;

interface Extra {
    queued: number;
    complete: number;
    skipped: number;
    failed: number;
}

export const useQueueHistory = (enabled: boolean, packageId: string, pageRequest: PageRequest) => {
    const options = pageRequestToUrlSearchParams(pageRequest);

    const fetch = useAuthenticatedRequest<PagedResultWithExtra<EndListingQueueItem, Extra>>({
        method: "get",
        url: `/packages/${packageId}/end-listing-queue?${options}`
    });

    return useQuery(["packages", packageId, "end-listing-queue", pageRequest], fetch, {
        enabled
    });
}

const QueueStatusIcon = ({ status }: { status: EndListingQueueStatus }) => {
    const style = { fontSize: '24px' };
    switch (status) {
        case EndListingQueueStatus.Queued:
            return <ClockCircleTwoTone style={style} />;
        case EndListingQueueStatus.Ended:
        case EndListingQueueStatus.Relisted:
            return <CheckCircleTwoTone style={style} twoToneColor="#52c41a" />;
        case EndListingQueueStatus.Skipped:
            return <StopTwoTone style={style} twoToneColor="#bfbfbf" />;
        case EndListingQueueStatus.Failed:
            return <WarningTwoTone style={style} twoToneColor="#eb2f96" />;
        default:
            return null;
    }
}

const QueueStatusFilter = ({
    prefixCls,
    visible,
    confirm,
    selectedKeys,
    setSelectedKeys,
    clearFilters,
    onFilter,
}: ExtendedFilterDropdownProps) => <KeyValueTableFilter
        prefixCls={prefixCls}
        visible={visible}
        confirm={confirm}
        selectedKeys={selectedKeys}
        setSelectedKeys={setSelectedKeys}
        clearFilters={clearFilters}
        onFilter={onFilter}
        clearTableFilterRequest={ClearTableFilterRequest.Status}
        keyValuePairs={{
            'Queued': EndListingQueueStatus.Queued,
            'Ended': EndListingQueueStatus.Ended,
            'Relisted': EndListingQueueStatus.Relisted,
            'Skipped': EndListingQueueStatus.Skipped,
            'Failed': EndListingQueueStatus.Failed
        }}
    />;

const EndListingQueueHistory = ({ packageId }: { packageId: string }) => {
    const [{ page, filterBy, withinDays, sort, pageSize }, setPageRequest] =
        useState<PageRequest>(InitialPageRequest);

    const { data, isLoading } = useQueueHistory(true, packageId, { page, filterBy, withinDays, sort, pageSize });
    const [filterDetail, setFilterDetail] = useState<{ status?: EndListingQueueStatus }>(
        {}
    );
    const { data: records = [], totalRecords, extra } = data || {};

    const onFilterUpdate = (key: string) => (value: any) => {
        setFilterDetail((prev) => ({ ...prev, [key]: value }));
    };

    const columns: ColumnsType<EndListingQueueItem> = [
        {
            title: "Status",
            dataIndex: "status",
            align: 'center',
            sorter: false,
            ...createCustomFilter(
                QueueStatusFilter,
                onFilterUpdate("status"),
                filterDetail.status
            ),
            render: (status) => <QueueStatusIcon status={status} />
        },
        {
            title: "SKU",
            dataIndex: "sku",
            sorter: false
        },
        {
            title: "Title",
            dataIndex: "cardTitle",
            sorter: false,
            render: (title, rec) => <>
                <MultiValueDisplay
                    title={title}
                    subTitle={rec.relistedItemId !== null ? `${rec.oldItemId} ➡ ${rec.relistedItemId}` : rec.oldItemId}
                />
                {rec.status === EndListingQueueStatus.Failed && <Text type="danger">{rec.exception ?? "Unable to end item"}</Text>}
            </>
        }
    ];

    const tableChanged = (
        // pagination
        { current: newPage, pageSize: newPageSize }: any,
        // filtering
        filters: Record<string, FilterValue | null>,
        // sorting
        { column, field, order }: any
    ) => {
        setPageRequest({
            page: newPageSize !== pageSize ? 1 : newPage,
            pageSize: newPageSize,
            filterBy: filters,
            withinDays,
            sort: column ? [field, order === "ascend"] : null,
        });
    };

    return <>
        <Space size={50} style={{ width: '100%', justifyContent: 'center' }}>
            <Statistic loading={isLoading} title="Queued" value={extra?.queued} prefix={<ClockCircleTwoTone />} />
            <Statistic loading={isLoading} title="Ended/Relisted" value={extra?.complete} prefix={<CheckCircleTwoTone twoToneColor="#52c41a" />} />
            <Statistic loading={isLoading} title="Ends Too Soon" value={extra?.skipped} prefix={<StopTwoTone twoToneColor="#bfbfbf" />} />
            <Statistic loading={isLoading} title="Failed" value={extra?.failed} prefix={<WarningTwoTone twoToneColor="#eb2f96" />} />
        </Space>
        <Table
            size="small"
            dataSource={records}
            rowKey={(r) => r.id}
            columns={columns}
            loading={isLoading}
            onChange={tableChanged}
            pagination={{
                total: totalRecords,
                current: page,
                pageSize,
                pageSizeOptions: PageSizeOptions,
                showSizeChanger: true,
                showQuickJumper: false,
                hideOnSinglePage: true
            }}
        />
    </>;
}

export default EndListingQueueHistory;